<script setup>
import {reactive, onMounted, computed} from "vue";
import {ApiWrapper} from "@/Utils/ApiWrapper";
import JsonCSV from "vue-json-csv";
import {Permissions} from "@/Utils/Permissions";
import {ToastUtil} from "@/Utils/ToastUtil";
import AddCollectionNoteComponent from "@/Components/Invoices/AddCollectionNoteComponent.vue";

const data = reactive({
  ageOptions: ['1 Week', '2 Weeks', '3 Weeks', '1 Month', '3 Months', '6 Months', '1 year'],
  totalItems: 0,
  options: {
    sortBy: [{key: '', order: 'desc'}],
    page: 1,
    itemsPerPage: 100
  },
  prevOptions: {},
  addingCollectionNotes: false,
  minAge: null,
  maxAge: '1 Month',
  client: null,
  clients: [],
  collectionNote: null,
  collectionNotes: null,
  data: [],
  editingCollectionNote: false,
  fields: ['invoice_num', 'claimnumber', 'name', 'rep_name', 'rep_email', 'amount', 'payments', 'pending', 'date', 'notesData'],
  headers: [
    { title: 'Actions', key: 'actions' },
    { title: 'Invoice #', align: 'left', key: 'invoice_num' },
    { title: 'Claim #', align: 'left', key: 'claimnumber' },
    { title: 'Client', key: 'name' },
    { title: 'Client Rep', key: 'rep_name', sortable: false },
    { title: 'Rep Email', key: 'rep_email', sortable: false },
    { title: 'Invoice Total', key: 'amount' },
    { title: 'Payment Total', key: 'payments' },
    { title: 'Pending Balance', key: 'pending' },
    { title: 'Date', key: 'date' },
    { title: 'Collection Notes', key: 'collection_notes' },
  ],
  invoiceId: null,
  items: [],
  // itemsCSV: [],
  labels: {
    invoice_num: 'Invoice #',
    claimnumber: 'Claim #',
    name: 'Client',
    amount: 'Invoice Total',
    payments: 'Payment Total',
    pending: 'Pending Balance',
    date: 'Date',
    notesData: 'Collection Notes'
  },
  loading: false,
  loadingCSV: false,
  search: '',
  viewNotesOption: false,

  addCollectionNoteStatus: 'closed',
  addCollectionNoteInvoiceId: '',
  addCollectionNoteInvoiceNum: '',
  addCollectionNoteUpdateNum: 0,
});

const formatTimestampByLocale = (timestamp) => {
  const date = new Date(timestamp)
  const timeOptions = {
    hour12: true,
    hour: "numeric",
    minute: "2-digit",
  }
  const dateOptions = {
    year: "numeric",
    month: "numeric",
    day: "numeric",
    timeZoneName:'short'
  }
  return date.toLocaleString('en', { ...dateOptions, ...timeOptions })
}

const saveCollectionNote = (invoiceId, note) => {
  data.loading = true
  let item = {
    invoiceid: invoiceId,
    note: note
  }
  ApiWrapper.post('invoices', 'update-collection-note', {
        item: item
      })
      .then((response) => {
        getData({
          page: data.options.page,
          itemsPerPage: data.options.itemsPerPage,
          sortBy: data.options.sortBy
        })
        ToastUtil.successMsg('Updated collection note');
      })
      .catch((err) => {
        ToastUtil.error(err);
        data.loading = false;
      })
}

const saveCollectionNotes = () => {
  data.loading = true
  let newNotes = []
  for(let x in data.items){
    if(data.items[x].bool){
      if(data.items[x].bool === true){
        let obj = {}
        obj.id = data.items[x].invoiceid
        obj.note = data.collectionNotes
        newNotes.push(obj)
      }
    }
  }
  ApiWrapper.post('invoices', 'update-collection-notes', {
        items: newNotes,
        note: data.collectionNotes
      })
      .then((response) => {
        getData({
          page: data.options.page,
          itemsPerPage: data.options.itemsPerPage,
          sortBy: data.options.sortBy
        })
        ToastUtil.successMsg('Updated collection note');
      })
      .catch((err) => {
        ToastUtil.error(err);
        data.loading = false;
      })
}

const removeCheckboxes = () => {
  for(let x in data.items){
    if(data.items[x].bool){
      if(data.items[x].bool === true){
        data.items[x].bool = false
      }
    }
  }
}

const checkAll = (value) => {
  for(let x in data.items){
    if(value){
      data.items[x].bool = true
    }else{
      data.items[x].bool = false
    }
  }
}

const getClients = () => {
  ApiWrapper.post('invoices', 'get-clients', {
      })
      .then((response) => {
          data.clients = response.clients;
      })
      .catch((err) => {
        ToastUtil.error(err);
      })
}

const getData = ({page, itemsPerPage, sortBy}) => {
  data.loading = true
  ApiWrapper.post('invoices', 'get-collection-data', {
        minAge: data.minAge,
        maxAge : data.maxAge,
        client: data.client,
        sortDesc: sortBy?.length > 0 ? sortBy[0].order : 'desc',
        itemsPerPage: itemsPerPage,
        sortBy: sortBy?.length > 0 ? sortBy[0].key : '',
        page: page
      })
      .then((response) => {
        data.items = response.data
        for(let x in response.data){
          response.data[x].bool = false
        }
        for(let x in data.items){
          let cd = data.items[x].collection_notes
          const notes = cd.map((item) => {
            const { note, name, created_at } = item;
            return `${note}. ${name} - ${formatTimestampByLocale(created_at)}`;
          });
          data.items[x].notesData = notes.join('\n')
        }

        for(let x in data.items){
          try{
            let d = formatTimestampByLocale(data.items[x].date)
            data.items[x].dateFormatted = data.items[x].date
            data.items[x].date = d
          }catch(e){
            // console.error(e);
          }
        }

        data.totalItems = 1*response.total
      })
      .catch((e)=>{
        ToastUtil.error(e);
      })
      .finally(()=>{
        data.prevOptions.page = page;
        data.prevOptions.itemsPerPage = itemsPerPage;
        data.prevOptions.sortBy = sortBy ? JSON.parse(JSON.stringify(sortBy)) : sortBy;

        data.loading = false;
      })
}

// const getDataCSV = () => {
//   data.loadingCSV = true
//   // this.$refs.export.data = null
//   let options = {
//     page: 1,
//     itemsPerPage: 4000,
//     sortBy: [],
//     sortDesc: []
//   }
//   ApiWrapper.post('invoices', 'get-collection-data', {
//         minAge: data.minAge,
//         maxAge : data.maxAge,
//         client: data.client,
//         options
//       })
//       .then((response) => {
//         if(response.data){
//           for(let x in response.data){
//             response.data[x].bool = false
//           }
//           let itemsCSV = response.data
//
//           for(let x in itemsCSV){
//             let cd = itemsCSV[x].collection_notes
//             const notes = cd.map((item) => {
//               const { note, name, created_at } = item;
//               return `${note}. ${name} - ${formatTimestampByLocale(created_at)}`;
//             });
//             itemsCSV[x].notesData = notes.join('\n')
//           }
//
//           this.$refs.export.data = itemsCSV
//           this.$refs.export.generate();
//
//           // for(x in data.itemsCSV){
//           //   try{
//           //     let d = $data.formatTimestampByLocale(data.itemsCSV[x].date)
//           //     data.itemsCSV[x].dateFormatted = d
//           //     // data.itemsCSV[x].date = d
//           //   }catch(e){
//           //   }
//           // }
//         }
//       })
//       .catch((e)=>{
//         ToastUtil.error(e);
//       })
//       .finally(()=>{
//         data.loadingCSV = false
//       })
// }

const filteredItems = computed(()=>{
  return  data.items.filter((item) => {
    return item.invoice_num.toLowerCase().includes(data.search.toLowerCase()) ||
        item.name.toLowerCase().includes(data.search.toLowerCase()) ||
        item.amount.toLowerCase().includes(data.search.toLowerCase()) ||
        item.payments.toLowerCase().includes(data.search.toLowerCase()) ||
        item.pending.toLowerCase().includes(data.search.toLowerCase()) ||
        item.notesData.toLowerCase().includes(data.search.toLowerCase()) ||
        formatTimestampByLocale(item.date).toLowerCase().includes(data.search.toLowerCase())

  })
});


onMounted(()=>{
  getData({
    page: data.options.page,
    itemsPerPage: data.options.itemsPerPage,
    sortBy: data.options.sortBy
  })
  getClients();
});

const updateSortBy = (sortBy) => {
  if(JSON.stringify(sortBy) !== JSON.stringify(data.prevOptions?.sortBy)) {
    data.options.sortBy = sortBy;

    getData({
      page: data.options.page,
      itemsPerPage: data.options.itemsPerPage,
      sortBy: sortBy
    });
  }
}

const updateItemsPerPage = (itemsPerPage) => {
  if(itemsPerPage !== data.prevOptions?.itemsPerPage){
    data.options.itemsPerPage = itemsPerPage;

    getData({
      page: data.options.page,
      itemsPerPage: itemsPerPage,
      sortBy: data.options.sortBy
    });
  }
}

const updatePage = (page) => {
  if(page !== data.prevOptions?.page){
    data.options.page = page;

    getData({
      page: page,
      itemsPerPage: data.options.itemsPerPage,
      sortBy: data.options.sortBy
    });
  }
}

const closeCollectionNoteModal = () => {
  data.addCollectionNoteStatus = 'closed';
  data.editingCollectionNote = false;
  data.invoiceId = null;
}

const saveCollectionNoteModal = (note) => {
  saveCollectionNote(data.addCollectionNoteInvoiceId, note);
  data.viewNotesOption = false;
  closeCollectionNoteModal();
}

const openCollectionNoteModal = (item) => {
  data.editingCollectionNote = true;
  data.invoiceId = item.invoiceid;
  data.addCollectionNoteInvoiceId = item.invoiceid;
  data.addCollectionNoteInvoiceNum = item.invoice_num;
  data.addCollectionNoteStatus = 'open';
  data.addCollectionNoteUpdateNum++;
}

</script>

<template>
  <v-container fluid>
    <v-card>
      <v-toolbar density="compact" flat color="white">
        <v-toolbar-title>Invoices</v-toolbar-title>
        <v-progress-linear
            :active="data.loading"
            :indeterminate="data.loading"
            absolute
            bottom
            color="primary"
        ></v-progress-linear>
        <v-divider class="mx-4" inset vertical></v-divider>

        <JsonCSV
            :data="data.items"
            name="Collection_Notes.csv"
            :labels="data.labels"
            :fields="data.fields"
        >
          <v-btn size="small" variant="elevated" color="primary" :loading="data.loadingCSV" :disabled="data.loadingCSV">
            <v-icon> mdi-file-download </v-icon>
          </v-btn>
        </JsonCSV>
        <v-autocomplete
            density="compact"
            class="px-5"
            clearable
            hide-details
            @update:modelValue="data.client=$event; getData({
                page: data.options.page,
                itemsPerPage: data.options.itemsPerPage,
                sortBy: data.options.sortBy
              })"
            :items="data.clients"
            item-title="text"
            item-value="value"
            label="Client"
            v-model="data.client"
            autocomplete="off">
        </v-autocomplete>
        <v-select
            density="compact"
            class="px-5"
            clearable
            hide-details
            @update:modelValue="data.minAge=$event; getData({
                page: data.options.page,
                itemsPerPage: data.options.itemsPerPage,
                sortBy: data.options.sortBy
              })"
            label="Minimum Invoice Age"
            v-model="data.minAge" :items="data.ageOptions"></v-select>
        <v-select
            density="compact"
            class="px-5"
            clearable
            hide-details
            @update:modelValue="data.maxAge=$event; getData({
                page: data.options.page,
                itemsPerPage: data.options.itemsPerPage,
                sortBy: data.options.sortBy
              })"
            label="Maximum Invoice Age"
            v-model="data.maxAge" :items="data.ageOptions"></v-select>
        <v-spacer/>
        <v-btn
            size="small"
            variant="elevated"
            color="primary"
            v-if="Permissions.checkPermissions(['invoices.update-collection-note'])"
            @click="data.addingCollectionNotes = true"
        >
          Add Collection Notes
        </v-btn>
      </v-toolbar>

      <v-row class="pa-4" v-if="data.addingCollectionNotes">
        <v-col>
          <v-btn
              size="small"
              variant="elevated"
              class="primary mx-2"
              @click="data.addingCollectionNotes=false; saveCollectionNotes(data.collectionNotes)"
          >Save</v-btn
          >
          <v-btn
              size="small"
              variant="elevated"
              class="mx-2"
              color="error"
              @click="data.collectionNotes = ''; data.addingCollectionNotes = false; removeCheckboxes()"
          >Cancel</v-btn
          >
          <v-btn
              size="small"
              variant="elevated"
              class="success mx-2"
              @click="checkAll(true)"
          >
            Check All
          </v-btn>
          <v-btn
              size="small"
              variant="elevated"
              class="mx-2"
              color="success"
              @click="checkAll(false)"
          >
            Uncheck All
          </v-btn>
          <v-text-field class="mt-3" style="min-height: 200px" label="Collection Notes" v-model.lazy="data.collectionNotes"></v-text-field>
        </v-col>
      </v-row>
      <v-row density="compact">
        <v-col cols="6">
          <v-text-field
              density="compact"
              filled
              v-model.lazy="data.search"
              append-inner-icon="mdi-magnify"
              label="Search"
              single-line
              hide-details
              class="ml-4"
          ></v-text-field>
        </v-col>
      </v-row>

      <v-data-table-server
          density="compact"
          :headers="data.headers"
          :items="filteredItems"
          :items-length="data.totalItems"
          class="elevation-1 mt-4"
          item-value="invoicenumber"
          loading-text="Loading... Please wait"
          :loading="data.loading"
          :items-per-page-options="[100, 200, 300, 400, 500]"

          :page="data.options.page"
          @update:page="updatePage"
          :items-per-page="data.options.itemsPerPage"
          @update:itemsPerPage="updateItemsPerPage"
          :sortBy="data.options.sortBy"
          @update:sortBy="updateSortBy"
      >
        <template v-slot:[`item.invoice_num`]="{ item }">
          <RouterLink :to="`/claim/${item.claimid}`">
            {{ item.invoice_num }}
          </RouterLink>
        </template>
        <template v-slot:[`item.claimnumber`]="{ item }">
          <RouterLink :to="`/claim/${item.claimid}`">
            {{ item.claimnumber }}
          </RouterLink>
        </template>
        <template v-slot:[`item.date`]="{ item }">
          <span> {{ item.dateFormatted }}</span>
        </template>
        <template v-slot:[`item.collection_notes`]="{ item }">
          <div v-for="(x, index) in item.collection_notes" :key="x.id">
              <div v-if="index === 0">
<!--                <td>{{ x.note }}</td>-->
                {{ x.note }}
                <br />
                <v-card-subtitle v-if="x.created_at" class="pa-0 card__subtitle">{{ x.name }} - {{ formatTimestampByLocale(x.created_at) }}</v-card-subtitle>
              </div>
              <div v-else-if="data.viewNotesOption === true && item.viewMoreNotes">
<!--                <td>{{ x.note }}</td>-->
                {{ x.note }}
                <br />
                <v-card-subtitle v-if="x.created_at" class="pa-0 card__subtitle">{{ x.name }} - {{ formatTimestampByLocale(x.created_at) }}</v-card-subtitle>
              </div>
          </div>
          <div v-if="item.collection_notes.length > 1 && item.collection_notes[0].note">
            <v-btn
                v-if="item.viewMoreNotes != true"
                variant="text"
                size="x-small"
                color="primary"
                class="mb-1"
                @click="data.viewNotesOption = true; item.viewMoreNotes = true"
            >
              View More
              <v-icon right dark>mdi-chevron-down</v-icon>
            </v-btn>
            <v-btn
                v-if="item.viewMoreNotes === true"
                variant="text"
                size="x-small"
                color="primary"
                class="mb-1"
                @click="data.viewNotesOption = false; item.viewMoreNotes = false"
            >
              View Less
              <v-icon right dark>mdi-chevron-up</v-icon>
            </v-btn>
          </div>
        </template>
        <template v-slot:[`item.actions`]="{ item }">
          <v-btn
              size="x-small"
              color="success"
              v-if="!data.addingCollectionNotes && item.selected !== true && Permissions.checkPermissions(['invoices.update-collection-note'])"
              @click="openCollectionNoteModal(item)"
          >
            Add Note
          </v-btn>
          <v-checkbox
              v-if="data.addingCollectionNotes && item.selected !== true"
              v-model="item.bool"
          ></v-checkbox>
        </template>
      </v-data-table-server>
    </v-card>
  </v-container>
  <AddCollectionNoteComponent :status="data.addCollectionNoteStatus" :invoice-num="data.addCollectionNoteInvoiceNum" :update-num="data.addCollectionNoteUpdateNum" @closed="closeCollectionNoteModal" @saved="saveCollectionNoteModal"/>
</template>

<style scoped>
.container {
  padding: 5px;
}
[v-cloak] {
  display: none;
}
.card__subtitle {
  font-size: 10px !important;
  color: black !important;
  word-break: break-word !important;
}
</style>